/**
 * "Rename playlist" dialog.
 *
 * The same shape as EditSongModal, for the same reason: transient draft state, keyed
 * by id so reopening on a different playlist reinitialises the field. Save is disabled
 * on an empty name rather than silently falling back to the old one — a name is the
 * only thing a playlist has, and a Save that appears to work while changing nothing
 * would be worse than a button that says it cannot.
 */
import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button, Modal } from '../ui';
import { TextField } from '../forms';
import { c } from '../../styles/theme';
import type { Playlist } from '../../types/domain';

export interface RenamePlaylistModalProps {
  /** The playlist being renamed, or null when closed. */
  playlist: Playlist | null;
  onClose: () => void;
  onSave: (name: string) => void;
}

function RenamePlaylistForm({
  playlist,
  onClose,
  onSave,
}: RenamePlaylistModalProps & { playlist: Playlist }) {
  const [name, setName] = useState(playlist.name);
  const trimmed = name.trim();

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <p style={{ fontWeight: 600, fontSize: 15, margin: 0, fontFamily: 'Space Grotesk' }}>
          Rename playlist
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label="Close"
        >
          <X size={16} color={c.text2} />
        </button>
      </div>

      {/* A real <form>, so Enter in the name field saves — the same fix Login.tsx,
          NewProjectForm.tsx, Ticket.tsx, Playlists.tsx and AddToPlaylistModal.tsx carry,
          with the same note: "a real <form> so Enter submits, which the prototype's div did
          not". Renaming is one field and one button, so Enter is the gesture, and it did
          nothing.

          Cancel is `type="button"` — the default inside a form is submit, and a Cancel that
          saved would be the worst possible version of this bug.

          The guard repeats the Save button's `disabled` because a disabled button does not
          block Enter-to-submit: without it, clearing the field and pressing Enter would call
          onSave('') on a playlist whose name is the only thing it has. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!trimmed) return;
          onSave(trimmed);
        }}
      >
        <TextField
          id="lyx-rename-playlist"
          label="Name"
          value={name}
          onChange={setName}
          marginBottom={18}
          style={{ padding: '8px 10px' }}
        />

        <Button type="submit" full disabled={!trimmed} style={{ marginBottom: 8 }}>
          <Check size={13} />
          Save name
        </Button>
        <Button type="button" full variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </form>
    </>
  );
}

export default function RenamePlaylistModal({
  playlist,
  onClose,
  onSave,
}: RenamePlaylistModalProps) {
  return (
    <Modal open={Boolean(playlist)} onClose={onClose} ariaLabel="Rename playlist">
      {playlist && (
        <RenamePlaylistForm
          key={playlist.id}
          playlist={playlist}
          onClose={onClose}
          onSave={onSave}
        />
      )}
    </Modal>
  );
}
